import { z } from 'zod';
import { EXAM_EDITIONS } from '@/modules/tenant/data/editions.mapper';
import { isTenantId } from '@/modules/tenant/domain/entities/tenant-ids';
import type {
  Submission,
  SubmissionKind,
  SubmissionRequest,
} from '../../domain/entities/submission.entity';

/** Uma resposta como o Primum Core recebe e devolve. */
export const ApiAnswerSchema = z.object({
  questionNumber: z.number().int().positive(),
  sourceId: z.string().nullable().optional(),
  alternativeIndex: z.number().int().min(0),
});

export type ApiAnswer = z.infer<typeof ApiAnswerSchema>;

export const ApiSubmissionSchema = z.object({
  answers: z.array(ApiAnswerSchema),
  submittedAt: z.string(),
});

/**
 * Os `questionId` do ensaio desta prova e edição, na ordem do mapper — é a
 * posição nesta lista que vira o `questionNumber` do envio.
 */
function ensaioQuestionIds(exam: string, edition: number): readonly string[] {
  const config = isTenantId(exam) ? EXAM_EDITIONS[exam][edition] : undefined;
  if (!config) throw new Error(`Edição desconhecida: ${exam} ${edition}`);
  return config.ensaioQuestionIds;
}

/**
 * Do formato do domínio (`Record`) para o da API (lista):
 *
 * - **ensaio**: a chave é o `questionId` da Aristo. Ele vai como `sourceId`, e
 *   o `questionNumber` é a posição dele no `ensaioQuestionIds` da edição — a
 *   numeração estável da edição, a mesma que o mapper garante não reordenar.
 * - **gabarito**: a chave é o número da questão na folha; sem `sourceId` (a
 *   folha não conhece o id da questão).
 */
export function toApiAnswers(
  kind: SubmissionKind,
  { exam, edition, answers }: SubmissionRequest,
): ApiAnswer[] {
  if (kind === 'gabarito') {
    return Object.entries(answers)
      .map(([number, alternativeIndex]) => ({
        questionNumber: Number(number),
        sourceId: null,
        alternativeIndex,
      }))
      .sort((a, b) => a.questionNumber - b.questionNumber);
  }

  const ids = ensaioQuestionIds(exam, edition);
  return Object.entries(answers)
    .map(([questionId, alternativeIndex]) => {
      const position = ids.indexOf(questionId);
      if (position < 0) {
        throw new Error(`Questão fora do ensaio desta edição: ${questionId}`);
      }
      return {
        questionNumber: position + 1,
        sourceId: questionId,
        alternativeIndex,
      };
    })
    .sort((a, b) => a.questionNumber - b.questionNumber);
}

/** Da API de volta ao domínio: ensaio por `sourceId`, gabarito por número. */
export function fromApiSubmission(
  kind: SubmissionKind,
  data: unknown,
): Submission {
  const parsed = ApiSubmissionSchema.parse(data);
  const answers: Record<string, number> = {};
  for (const answer of parsed.answers) {
    const key =
      kind === 'ensaio' && answer.sourceId
        ? answer.sourceId
        : String(answer.questionNumber);
    answers[key] = answer.alternativeIndex;
  }
  return { answers, submittedAt: parsed.submittedAt };
}
